import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Box, Button, Typography } from '@mui/material'
import { setAlertQueue } from 'cache/alerts'
import { setIsDealLocked } from 'cache/deal-status'
import Loader from 'components/Loader/Loader'
import { useLockDeal } from 'hooks/use-deal-lock'
import { useSaveDecisionEditPageDetails } from 'hooks/use-save-decision-edit-page-details'
import { useUnlockDealOnUnload } from 'hooks/use-unlock-deal-on-unload'
import { useUserProfile } from 'hooks/use-user-profile'
import { useClearDecisionPageCache } from 'queries/clear-decision-page-cache'
import { useResetDecisionPageCache } from 'queries/reset-decision-page'
import { CurrencyConvertLineItem } from 'queries/useCurrencyConvertEditPage'
import { MessageType } from 'ts/enums/messageType.enums'
import ComparisonTabs from './components/ComparisonTabs'
import { CurrencyToggle } from './components/CurrencyToggle'
import { GridViewToggle, GridViewType } from './components/GridViewToggle'
import { ProductLineGrid } from './components/ProductLineGrid'
import { ProductsDetailsGrid } from './components/ProductsDetailsGrid'
import { transformProductsDetailsData } from './components/ProductsDetailsGrid/utils/transformProductsDetailsData'
import { PL_GUIDANCE_TYPES } from './constants'
import { useDataPointSelections } from './hooks/useDataPointSelections'
import { useDealId } from './hooks/useDealId'
import { useDecisionEditPageData } from './hooks/useDecisionEditPageData'
import { useStyles } from './styles/DecisionEditPage.styles'
import { getApprovalOptions } from './utils/getAllowedApprovalTypes'

const DecisionEditPage = () => {
  const { classes } = useStyles()
  const history = useHistory()
  const { dealNumber, decisionType, decisionReason, emailId } = useParams<{
    dealNumber: string
    decisionType: string
    decisionReason: string
    emailId: string
  }>()

  const [gridView, setGridView] = useState<GridViewType>('PL')
  const [plCurrencyData, setPlCurrencyData] = useState<CurrencyConvertLineItem[] | null>(null)
  const [pnCurrencyData, setPnCurrencyData] = useState<CurrencyConvertLineItem[] | null>(null)
  const [isUSDSelected, setIsUSDSelected] = useState(false)
  const [isCurrencyLoading, setIsCurrencyLoading] = useState(false)
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false)

  const dealId = useDealId()
  const { data, loading, refreshEditPageData } = useDecisionEditPageData(dealId)
  const { clearCache } = useClearDecisionPageCache()
  const { saveDecisionEditPageDetails, loading: saveLoading } = useSaveDecisionEditPageDetails()
  const { userId, userEmail } = useUserProfile()
  const { lockDealMutation } = useLockDeal()
  const { resetCache } = useResetDecisionPageCache()

  // Lock on mount; skip if arriving from the decision page (deal is already locked).
  // Re-locks after a page refresh when the sessionStorage flag is absent.
  useEffect(() => {
    if (!dealId || !userId) return

    setIsDealLocked(true)
    const fromDecisionPage = sessionStorage.getItem('fromDecisionPage') === 'true'
    sessionStorage.removeItem('fromDecisionPage')

    if (fromDecisionPage) return

    lockDealMutation(dealId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, userId])

  useUnlockDealOnUnload({ dealId, userId })

  const dealCubeHistoryDetails = useMemo(
    () => data?.dealCubeHistoryProductDetails?.dealCubeHistoryDetails ?? [],
    [data?.dealCubeHistoryProductDetails?.dealCubeHistoryDetails]
  )
  const approvalOptions = useMemo(
    () => getApprovalOptions(dealCubeHistoryDetails),
    [dealCubeHistoryDetails]
  )

  const {
    currentActiveTabs,
    currentApprovalSelections,
    currentGuidanceSelections,
    currentColorMap,
    currentIsLimitReached,
    plActiveTabs,
    pnActiveTabs,
    sanitizedPlApprovalSelections,
    sanitizedPnApprovalSelections,
    plGuidanceSelections,
    pnGuidanceSelections,
    plResolvedColorMap,
    pnResolvedColorMap,
    isSelectionsEmpty,
    handleTabsChange,
    handleGuidanceTypesChange,
    handleApprovalTypesChange,
    resetSelections,
  } = useDataPointSelections(gridView, approvalOptions)

  const pnData = data?.combinedFlatProducts
    ? transformProductsDetailsData(data.combinedFlatProducts, dealCubeHistoryDetails, {
        totalBdNetPriceAmount: data?.totalBdNetPriceAmount,
        totalEstimatedQuantity: data?.totalEstimatedQuantity,
      })
    : null

  const plData = data?.plTotalDynamicResponse?.plTotalDynamic ?? undefined
  const plTotals = data?.plTotalDynamicResponse
    ? {
        totalNetRevenue: data.plTotalDynamicResponse.totalNetRevenue,
        totalDiscountPercentage: data.plTotalDynamicResponse.totalDiscountPercentage,
        totalOGMPercentage: data.plTotalDynamicResponse.totalOGMPercentage,
      }
    : undefined

  const dealCurrency = data?.dealHeaderInformation?.isoCurrencyCode ?? 'EUR'

  const handleCurrencyDataChange = useCallback(
    (
      plData: CurrencyConvertLineItem[] | null,
      pnData: CurrencyConvertLineItem[] | null,
      isUSD: boolean
    ) => {
      setPlCurrencyData(plData)
      setPnCurrencyData(pnData)
      setIsUSDSelected(isUSD)
    },
    []
  )

  const handleCurrencyLoadingChange = useCallback((isLoading: boolean) => {
    setIsCurrencyLoading(isLoading)
  }, [])

  const handleCancelClick = useCallback(() => {
    const redirectPath = `/workflow/${dealNumber}/${decisionType}/${decisionReason}/${emailId}`

    if (!hasUnsavedEdits) {
      history.push(redirectPath)
      return
    }

    setAlertQueue({
      type: MessageType.INFO,
      message: 'All the changes will be removed. Are you sure you want to cancel all the changes?',
      onOk: async () => {
        if (dealId) {
          await clearCache({ variables: { dealId } })
          history.push(redirectPath)
        }
      },
    })
  }, [
    clearCache,
    dealId,
    history,
    dealNumber,
    decisionType,
    decisionReason,
    emailId,
    hasUnsavedEdits,
  ])

  const handleSaveAndApply = useCallback(() => {
    const parsedDealNumber = Number(dealNumber)

    if (!dealId || !dealNumber || !Number.isFinite(parsedDealNumber) || !userId || !userEmail) {
      setAlertQueue({
        type: MessageType.ERROR,
        message: 'Missing required information to save changes.',
      })
      return
    }

    setAlertQueue({
      type: MessageType.INFO,
      message: 'Are you sure you want to save and apply all the changes?',
      onOk: async () => {
        try {
          const result = await saveDecisionEditPageDetails({
            variables: {
              input: {
                dealId,
                dealNumber: parsedDealNumber,
                userId,
                userEmailId: userEmail,
              },
            },
          })

          if (result.data?.saveDecisionEditPageDetails?.isSuccess) {
            setAlertQueue({
              type: MessageType.SUCCESS,
              message:
                result.data.saveDecisionEditPageDetails.message ?? 'Changes saved successfully.',
            })
            setHasUnsavedEdits(false)
            history.push(`/workflow/${dealNumber}/${decisionType}/${decisionReason}/${emailId}`)
          } else {
            setAlertQueue({
              type: MessageType.ERROR,
              message:
                result.data?.saveDecisionEditPageDetails?.errorMessage ??
                'Failed to save changes. Please try again.',
            })
          }
        } catch (error) {
          setAlertQueue({
            type: MessageType.ERROR,
            message: 'An error occurred while saving changes. Please try again.',
          })
          console.error('Error saving decision edit page details:', error)
        }
      },
    })
  }, [
    dealId,
    dealNumber,
    userId,
    userEmail,
    saveDecisionEditPageDetails,
    history,
    decisionType,
    decisionReason,
    emailId,
  ])

  const isAllReset = useMemo(
    () =>
      !hasUnsavedEdits &&
      isSelectionsEmpty &&
      plCurrencyData === null &&
      pnCurrencyData === null &&
      !isUSDSelected,
    [hasUnsavedEdits, isSelectionsEmpty, plCurrencyData, pnCurrencyData, isUSDSelected]
  )

  const handleResetClick = useCallback(async () => {
    if (!dealId) return

    try {
      const result = await resetCache({ variables: { dealId } })
      if (result.data?.resetDecisionPage?.isSuccess) {
        setHasUnsavedEdits(false)
        resetSelections()
        setPlCurrencyData(null)
        setPnCurrencyData(null)
        setIsUSDSelected(false)
        await refreshEditPageData()
      } else {
        setAlertQueue({
          type: MessageType.ERROR,
          message: `Failed to reset decision page data for deal ${dealNumber}. Please try again.`,
        })
      }
    } catch (error) {
      console.error('Error resetting decision page cache:', error)
      setAlertQueue({
        type: MessageType.ERROR,
        message: `An error occurred while resetting decision page data for deal  ${dealNumber}. Please try again.`,
      })
    }
  }, [resetCache, dealId, refreshEditPageData, dealNumber, resetSelections])

  return (
    <Box className={classes.mainContainer}>
      <Loader open={loading || isCurrencyLoading || saveLoading} />
      <Box>
        <Typography className={classes.title}>Business Pricing Solution</Typography>
        <Box className={classes.container}>
          <Box className={classes.toolbar}>
            <GridViewToggle value={gridView} onChange={setGridView} />
            <CurrencyToggle
              dealCurrency={dealCurrency}
              checked={isUSDSelected}
              onCurrencyDataChange={handleCurrencyDataChange}
              onLoadingChange={handleCurrencyLoadingChange}
            />
            <Box className={classes.tabsContainer}>
              <Typography className={classes.dataPointsLabel}>Data Points: </Typography>
              <ComparisonTabs
                activeTabs={currentActiveTabs}
                onTabChange={handleTabsChange}
                selectedApprovalTypes={currentApprovalSelections}
                onApprovalTypesChange={handleApprovalTypesChange}
                selectedGuidanceTypes={currentGuidanceSelections}
                onGuidanceTypesChange={handleGuidanceTypesChange}
                approvalOptions={approvalOptions}
                allowedGuidanceTypes={gridView === 'PL' ? PL_GUIDANCE_TYPES : undefined}
                colorMap={currentColorMap}
                isLimitReached={currentIsLimitReached}
              />
            </Box>
            <Button
              className={classes.resetBtn}
              onClick={handleResetClick}
              disabled={saveLoading || isAllReset}
            >
              Reset All
            </Button>
          </Box>
          {gridView === 'PL' ? (
            <ProductLineGrid
              dealId={dealId}
              data={plData}
              totals={plTotals}
              currencyData={plCurrencyData ?? undefined}
              isUSDSelected={isUSDSelected}
              activeTabs={plActiveTabs}
              selectedApprovalTypes={sanitizedPlApprovalSelections}
              selectedGuidanceTypes={plGuidanceSelections}
              colorMap={plResolvedColorMap}
              approvalOptions={approvalOptions}
              dealCubeHistoryDetails={dealCubeHistoryDetails}
              onRefresh={refreshEditPageData}
              onEditSuccess={() => setHasUnsavedEdits(true)}
            />
          ) : (
            <ProductsDetailsGrid
              data={pnData}
              currencyData={pnCurrencyData ?? undefined}
              isUSDSelected={isUSDSelected}
              activeTabs={pnActiveTabs}
              selectedApprovalTypes={sanitizedPnApprovalSelections}
              selectedGuidanceTypes={pnGuidanceSelections}
              colorMap={pnResolvedColorMap}
              approvalOptions={approvalOptions}
              dealId={dealId}
              onRefresh={refreshEditPageData}
              onEditSuccess={() => setHasUnsavedEdits(true)}
            />
          )}
        </Box>
        <Box className={classes.actionsContainer}>
          <Button
            className={classes.actionBtn}
            variant="outlined"
            color="primary"
            onClick={handleCancelClick}
          >
            Cancel
          </Button>
          <Button
            className={classes.actionBtn}
            variant="contained"
            color="primary"
            disabled={isUSDSelected || saveLoading || !hasUnsavedEdits}
            onClick={handleSaveAndApply}
          >
            Save and apply
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default DecisionEditPage
